import { 
  VStack, 
  HStack, 
  Button, 
  Text, 
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Icon,
  Center,
  Switch,
  FormControl,
  FormLabel,
  Tooltip
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useAudioContext } from '../context/AudioContext';
import { useDisplayNames } from '../context/DisplayNamesContext';
import { FaPlay, FaCopy } from 'react-icons/fa';
import { naturalSortNumbers } from '../utils/sorting';

interface GuessResult {
  trackName: string;
  wasCorrect: boolean;
}

interface GuessTheTrackViewProps {
  playerNumberToFileId?: Map<number, string>;
}

const GuessTheTrackView: React.FC<GuessTheTrackViewProps> = ({ playerNumberToFileId }) => {
  const { audioElements, selectedPlayer, selectPlayer, playAll, blindTestMode, blindTestIncludedPlayers } = useAudioContext();
  const { getCustomDisplayName } = useDisplayNames();
  const [guesses, setGuesses] = useState<GuessResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [lastPlayedTrack, setLastPlayedTrack] = useState<number | null>(null);
  const [allowRepeats, setAllowRepeats] = useState(false);
  const toast = useToast();

  const handleGuess = (guessedTrackName: string) => {
    const currentTrack = audioElements.get(selectedPlayer)?.title || '';
    const isCorrect = guessedTrackName === currentTrack;

    // Add the guess to our results
    setGuesses(prev => [...prev, {
      trackName: currentTrack,
      wasCorrect: isCorrect
    }]);

    // Visual feedback
    const button = document.querySelector(`[data-track-name="${guessedTrackName}"]`);
    if (button) {
      button.classList.add(isCorrect ? 'correct-guess' : 'incorrect-guess');
      setTimeout(() => {
        button.classList.remove('correct-guess', 'incorrect-guess');
      }, 500);
    }

    // Store the current track as last played
    setLastPlayedTrack(selectedPlayer);

    // Trigger random switch after a short delay
    setTimeout(() => {
      // Get all included player numbers
      const includedPlayers = Array.from({ length: audioElements.size }, (_, i) => i + 1)
        .filter(playerNum => blindTestIncludedPlayers.get(playerNum) ?? true);
      
      if (includedPlayers.length === 0) return; // No included players
      
      // If allowRepeats is false, exclude the current player
      const availablePlayers = allowRepeats 
        ? includedPlayers 
        : includedPlayers.filter(num => num !== selectedPlayer);
      
      // If no available players (shouldn't happen if allowRepeats is true), fall back to all included
      const playersToChooseFrom = availablePlayers.length > 0 ? availablePlayers : includedPlayers;
      
      const newPlayer = playersToChooseFrom[Math.floor(Math.random() * playersToChooseFrom.length)];
      selectPlayer(newPlayer);
    }, 500);
  };

  const calculateStats = () => {
    const trackStats = new Map<string, { correct: number, total: number }>();
    
    // Initialize stats for all tracks
    audioElements.forEach((audio) => {
      trackStats.set(audio.title, { correct: 0, total: 0 });
    });

    // Calculate stats for each track
    guesses.forEach(guess => {
      const stats = trackStats.get(guess.trackName);
      if (stats) {
        stats.total++;
        if (guess.wasCorrect) {
          stats.correct++;
        }
      }
    });

    const total = guesses.length;
    const correct = guesses.filter(g => g.wasCorrect).length;
    
    return {
      total,
      correct,
      percentage: total > 0 ? Math.round((correct / total) * 100) : 0,
      trackStats
    };
  };

  const copyResults = async () => {
    const stats = calculateStats();
    let text = `Guess The Track - Results\n\nOverall Accuracy: ${stats.percentage}%\nCorrect Guesses: ${stats.correct}/${stats.total}\n\nTrack Results:\n`;
    
    Array.from(stats.trackStats.entries())
      .filter(([_, trackStats]) => trackStats.total > 0)
      .forEach(([trackName, trackStats]) => {
        const accuracy = trackStats.total === 0 ? 0 : Math.round((trackStats.correct / trackStats.total) * 100);
        text += `${trackName}: ${accuracy}% (${trackStats.correct}/${trackStats.total})\n`;
      });

    try {
      // Try the modern clipboard API first
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Results copied!',
        description: 'The results have been copied to your clipboard',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      // Fallback for mobile: Create a temporary textarea
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';  // Avoid scrolling to bottom
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        document.execCommand('copy');
        toast({
          title: 'Results copied!',
          description: 'The results have been copied to your clipboard',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        toast({
          title: 'Copy failed',
          description: 'Unable to copy results to clipboard',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }

      document.body.removeChild(textarea);
    }
  };

  const handleContinue = () => {
    setShowResults(false);
    setGuesses([]);  // Reset the guesses
  };

  const handleStart = () => {
    setHasStarted(true);
    // Start playing a random track from included players
    const includedPlayers = Array.from({ length: audioElements.size }, (_, i) => i + 1)
      .filter(playerNum => blindTestIncludedPlayers.get(playerNum) ?? true);
    
    if (includedPlayers.length === 0) return;
    
    const newPlayer = includedPlayers[Math.floor(Math.random() * includedPlayers.length)];
    selectPlayer(newPlayer);
    playAll();
  };

  // Handle number key presses
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const numKey = parseInt(event.key);
      if (!isNaN(numKey) && numKey >= 1 && numKey <= audioElements.size) {
        // Only allow guessing for included players
        const isPlayerIncluded = blindTestIncludedPlayers.get(numKey) ?? true;
        if (isPlayerIncluded) {
          const trackName = audioElements.get(numKey)?.title;
          if (trackName) {
            handleGuess(trackName);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [audioElements, handleGuess, blindTestIncludedPlayers]);

  // Reset component when tab is selected
  useEffect(() => {
    setGuesses([]);
    setShowResults(false);
    setHasStarted(false);
    setAllowRepeats(false);
  }, [blindTestMode]);

  if (showResults) {
    const stats = calculateStats();
    return (
      <Box overflowY="auto" px={4}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between" align="center">
            <Text fontSize="xl" color="accent.400">
              Overall Accuracy: {stats.percentage}% ({stats.correct}/{stats.total})
            </Text>
            <Button
              leftIcon={<FaCopy />}
              onClick={copyResults}
              size="sm"
              colorScheme="accent"
              variant="outline"
            >
              Copy Results
            </Button>
          </HStack>

          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Track Name</Th>
                <Th isNumeric>Accuracy</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Array.from(stats.trackStats.entries())
                .filter(([_, trackStats]) => trackStats.total > 0)
                .map(([trackName, trackStats]) => (
                  <Tr key={trackName}>
                    <Td>{trackName}</Td>
                    <Td isNumeric color="accent.400">
                      {trackStats.total === 0 ? '0%' : `${Math.round((trackStats.correct / trackStats.total) * 100)}%`}
                      <Text as="span" color="gray.500" fontSize="sm" ml={2}>
                        ({trackStats.correct}/{trackStats.total})
                      </Text>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>

          <Button
            onClick={handleContinue}
            colorScheme="accent"
            alignSelf="center"
          >
            Continue Guessing
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" w="full" p={2}>
      <Center px={4}>
        <Text color="gray.300" fontSize="sm" textAlign="center">
          {!hasStarted ? (
            <>
              Click <Text as="span" color="accent.400" display="inline">Start</Text> to begin guessing tracks
            </>
          ) : (
            "Press Play and guess the track (click or press 1-9)."
          )}
        </Text>
      </Center>

      <VStack spacing={3} flex={1} justify="center" w="full">
        {Array.from(audioElements.entries())
          .filter(([playerNumber]) => blindTestIncludedPlayers.get(playerNumber) ?? true)
          .sort(([a], [b]) => naturalSortNumbers(a, b))
          .map(([playerNumber, audio]) => (
            <Text
              key={playerNumber}
              onClick={() => hasStarted && handleGuess(audio.title)}
              fontSize="lg"
              color="gray.400"
              cursor={hasStarted ? "pointer" : "default"}
              textAlign="center"
              transition="color 0.2s"
              px={4}
              data-track-name={audio.title}
              sx={{
                '&.correct-guess': {
                  color: 'green.400',
                  transition: 'color 0.5s ease'
                },
                '&.incorrect-guess': {
                  color: 'red.400',
                  transition: 'color 0.5s ease'
                }
              }}
            >
              {playerNumber}. {(() => {
                const fileId = playerNumberToFileId?.get(playerNumber);
                const customName = fileId ? getCustomDisplayName(fileId) : '';
                const displayName = customName || audio.title;
                return displayName ? displayName.replace(/:/g, '/') : '';
              })()}
            </Text>
          ))}
      </VStack>

      <Button
        mt={4}
        onClick={hasStarted ? () => setShowResults(true) : handleStart}
        colorScheme="accent"
        variant="outline"
        size="lg"
        width="200px"
        mx="auto"
        isDisabled={hasStarted && guesses.length === 0}
        _hover={{
          transform: 'scale(1.05)',
          borderColor: 'accent.400',
          bg: 'transparent'
        }}
        _active={{
          transform: 'scale(0.95)',
          borderColor: 'accent.500',
          bg: 'transparent'
        }}
      >
        {hasStarted ? "Show Results" : "Start"}
      </Button>

      <Tooltip 
        label={allowRepeats ? "Random switches may select the same track again" : "Random switches will always select a different track"} 
        placement="top"
        hasArrow
      >
        <FormControl display="flex" alignItems="center" justifyContent="center" mt={4}>
          <FormLabel htmlFor="allow-repeats-guess" mb={0} color="gray.300" fontSize="sm" cursor="help">
            Allow repeats
          </FormLabel>
          <Switch
            id="allow-repeats-guess"
            isChecked={allowRepeats}
            onChange={(e) => setAllowRepeats(e.target.checked)}
            colorScheme="accent"
          />
        </FormControl>
      </Tooltip>
    </VStack>
  );
};

export default GuessTheTrackView; 